import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Field, FieldArray, Form, Formik } from 'formik';
import { Select, TreeSelect } from 'antd';
import { Category } from '../../../models/Category/Category';
import { Filter } from '../../../models/Filter/Filter';
import { Attribute } from '../../../models/Attribute/Attribute';
import { Button, Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { InputField } from '../../components/InputField/InputField';
import { ErrorMessageLabel } from '../../components/ErrorMessageLabel/ErrorMessageLabel';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { ToastContainer, toast } from 'react-toastify';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { AttributeTypes } from '../../../Shop/components/Attributes/AttributeValue';
import style from './create-attribute.module.scss';
import ColorBox from '../../components/ColorBox/ColorBox';

// --- TYPES ---
interface AttributeFilterValue {
  id: number;
  // settings?: any; // Uncomment if you need per-pivot settings later
}

interface AttributeValueForm {
  value: string;
  filter_values: AttributeFilterValue[];
  settings?: any;
  type?: AttributeTypes;
}

interface EditAttributeValues {
  categories: number[];
  name: string;
  type: AttributeTypes;
  values: AttributeValueForm[];
}

function EditAttribute() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();

  const { data: attribute, isLoading } = useQuery(
    ['attribute', id],
    () => Attribute.get(id!),
    { enabled: typeof id !== 'undefined' }
  );

  const { data: filters, isLoading: isFiltersLoading } = useQuery(
    ['filters'],
    () => Filter.all('values')
  );
  const { data: categories } = useQuery(['categories'], () => Category.all());

  // Build initial values with filter_values as array of objects
  const initialValues: EditAttributeValues = {
    categories: attribute?.categories?.map(({ id }) => id) ?? [],
    name: attribute?.name ?? '',
    type: attribute?.options?.[0]?.type ?? AttributeTypes.TEXT,
    values:
      attribute?.options?.map((option) => ({
        value: option.value,
        filter_values:
          option.filterValues?.map((fv: any) =>
            typeof fv === 'object' && fv !== null && 'id' in fv
              ? { id: fv.id }
              : { id: fv }
          ) ?? [],
        settings: option.settings,
        type:
          option.type ?? attribute?.options?.[0]?.type ?? AttributeTypes.TEXT
      })) ?? []
  };

  if (isLoading || isFiltersLoading) {
    return <h4>Loading...</h4>;
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <h1 className="cs-title">Izmena Atributa</h1>
      <Row>
        <Col lg={12}>
          <Formik<EditAttributeValues>
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object({
              name: Yup.string().max(255, 'name.max').required('name.required'),
              type: Yup.string().required(),
              categories: Yup.array().of(Yup.number()).optional(),
              values: Yup.array()
                .min(1, 'Unesite bar jednu vrednost!')
                .of(
                  Yup.object().shape({
                    value: Yup.string().required('Vrednost je obavezna!'),
                    filter_values: Yup.array()
                      .of(
                        Yup.object().shape({
                          id: Yup.number().required()
                        })
                      )
                      .required(),
                    settings: Yup.mixed().optional()
                  })
                )
            })}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              try {
                await attribute?.update({
                  ...values,
                  presentation_type: values.type,
                  values: values.values.map((v) => ({
                    value: v.value,
                    filter_values: v.filter_values.map((fv) =>
                      typeof fv === 'object' && fv !== null ? fv.id : fv
                    ),
                    type: values.type,
                    settings: v.settings ?? null
                  }))
                });
                toast.success('Atribut uspešno izmenjen!');
                await client.invalidateQueries({ queryKey: ['attributes'] });
                await client.invalidateQueries({ queryKey: ['attribute', id] });
                navigate('/admin/attributes/list');
              } catch (error: any) {
                toast.error(
                  error?.response?.data?.message ||
                    'Greška pri snimanju atributa.'
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue }) => (
              <Form autoComplete="off" className="cs-form-wrapper">
                <FormGroup>
                  <FormLabel>Izaberi Kategoriju</FormLabel>
                  <TreeSelect
                    treeCheckable
                    showSearch
                    treeDefaultExpandAll
                    placeholder="Izaberi kategoriju"
                    treeData={categories ?? []}
                    value={values.categories}
                    onChange={(value) =>
                      setFieldValue('categories', value, true)
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Tip Prikaza Atributa</FormLabel>
                  <Field
                    placeholder="Izaberi"
                    name="type"
                    component={Select}
                    mode={'single'}
                    defaultValue={values.type}
                    onChange={(value: AttributeTypes) =>
                      setFieldValue(`type`, value, true)
                    }
                  >
                    <Select.OptGroup>
                      <Select.Option value={AttributeTypes.TEXT}>
                        Tekst
                      </Select.Option>
                      <Select.Option value={AttributeTypes.COLOR}>
                        Boja
                      </Select.Option>
                      <Select.Option value={AttributeTypes.IMAGE}>
                        Slika
                      </Select.Option>
                    </Select.OptGroup>
                  </Field>
                  <ErrorMessageLabel name="type" />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Vrednost Atributa</FormLabel>
                  <FieldArray name="values">
                    {({ push, remove }) => (
                      <>
                        {values.values?.map((val, index) => (
                          <Row key={index} className="mb-3">
                            <Col className="cs-attribute-name-wrapper">
                              <InputField
                                placeholder={
                                  values.type === AttributeTypes.COLOR
                                    ? 'Unesite hex vrednost boje'
                                    : 'Unesite vrednost'
                                }
                                name={`values.${index}.value`}
                                autoComplete="off"
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setFieldValue(`values.${index}.value`, value);
                                }}
                              />
                              {values.type === AttributeTypes.COLOR && (
                                <div className={style.colorPickerInput}>
                                  <ColorBox
                                    value={val.value}
                                    onChange={(color: any) =>
                                      setFieldValue(
                                        `values.${index}.settings`,
                                        color
                                      )
                                    }
                                  />
                                </div>
                              )}
                              <ErrorMessageLabel
                                className="cs-error-msg"
                                name={`values.${index}.value`}
                              />
                            </Col>
                            <Col className="cs-attribute-filters-wrapper">
                              <Field
                                placeholder="Izaberi"
                                name={`values.${index}.filter_values`}
                                component={Select}
                                mode="multiple"
                                onChange={(
                                  filterValueIds:
                                    | number[]
                                    | AttributeFilterValue[]
                                ) => {
                                  // Map to objects for form state (preserve future settings)
                                  const selected: AttributeFilterValue[] = (
                                    filterValueIds as (
                                      | number
                                      | AttributeFilterValue
                                    )[]
                                  ).map((fid) => {
                                    if (
                                      typeof fid === 'object' &&
                                      fid !== null &&
                                      'id' in fid
                                    ) {
                                      return fid as AttributeFilterValue;
                                    }
                                    const existing = val.filter_values?.find(
                                      (fv) => fv.id === fid
                                    );
                                    if (existing) return existing;
                                    return { id: fid as number };
                                  });
                                  setFieldValue(
                                    `values.${index}.filter_values`,
                                    selected
                                  );
                                }}
                                value={val.filter_values?.map((fv) =>
                                  typeof fv === 'object' ? fv.id : fv
                                )}
                              >
                                {filters?.map((f) =>
                                  !f.options ? null : (
                                    <Select.OptGroup key={f.id} label={f.name}>
                                      {f.options.map((opt) => (
                                        <Select.Option
                                          key={opt.id}
                                          value={opt.id}
                                        >
                                          {opt.value}
                                        </Select.Option>
                                      ))}
                                    </Select.OptGroup>
                                  )
                                )}
                              </Field>
                              <Button
                                className="JHover"
                                variant="outline-light"
                                onClick={() => remove(index)}
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        <Button
                          variant="outline-light"
                          className="btn-lg JHover"
                          onClick={() =>
                            push({
                              value: '',
                              filter_values: [],
                              settings: null
                            })
                          }
                        >
                          <i className="fa-solid fa-circle-plus"></i>
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </FormGroup>
                <ButtonPrimary type="submit">Sačuvaj</ButtonPrimary>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </>
  );
}

export default EditAttribute;
